import { isMicrosoft365TurnCode, isMicrosoft365WriteTool, microsoft365ToolOf, type Microsoft365WriteTool, type TurnFailureDto } from '@dbrain/shared';
import type { ApprovalView, ChatItem, ChatState, SourceView } from './conversation-state.js';

/**
 * The steps of a turn (docs/design/mockups/Adimlar.dc.html, approved by the
 * user on 2026-10-27). While the answer is being prepared one line shows
 * only the current step; when it is ready the line becomes a summary. Both
 * open to the list of steps with their timings, and each step to its
 * details (search results, the page read, the thought). An unreadable page
 * is a quiet grey line. Reasoning steps have fixed names (D-77): before
 * anything was found they examine the question, after a search, a read or
 * another tool's result they evaluate the findings; the model's own words
 * are only in the details. Built from the same events as
 * the rest of the conversation, so a reload shows the same steps.
 *
 * Skill loads and document searches are steps of the line too, counted in
 * its summary ("3 skill · 1 belge araması"); a turn that loaded more than
 * three skills says so in its list. Waiting for documents still being read
 * is a step of its own (D-92).
 */
export type StepKind = 'think' | 'answer' | 'search' | 'read' | 'docs' | 'doc' | 'skill' | 'prepare' | 'helper' | 'file' | 'command' | 'look' | 'tool';

export type Unreadable = 'closed' | 'down' | 'timeout' | 'notFound' | 'serverError' | 'invalid' | 'blocked' | 'redirects' | 'other';

/** D-77: reasoning before anything was found examines the question; after a tool's result, it evaluates the findings. */
export type ThinkPhase = 'examine' | 'evaluate';

export interface SearchHit {
  title: string;
  url: string;
  host: string;
}

export type StepDetails =
  | { kind: 'results'; results: SearchHit[]; more: number }
  | { kind: 'page'; url: string; title: string | null; excerpt: string }
  | { kind: 'thought'; text: string }
  | { kind: 'text'; text: string }
  /** a loaded skill: which part, what it lets run without asking, whether it was there already or the user ran it */
  | { kind: 'skill'; part: number; parts: number; grants: string[]; reused: boolean; byUser: boolean };

export interface StepView {
  id: string;
  kind: StepKind;
  status: 'running' | 'done' | 'failed';
  tool: string | null;
  /** what the step is about: the query, the site, the file, the purpose */
  subject: string;
  /** the server's plain-language title (D-35), for tools without their own wording */
  title: string;
  durationMs: number | null;
  /** an unreadable page: a quiet grey line, no details */
  unreadable: Unreadable | null;
  /** grouped unreadable pages */
  count: number;
  details: StepDetails | null;
  /** a reasoning step: what it is called (D-77) */
  phase?: ThinkPhase;
  /** D-179: a connection's tool that ran without asking, and why */
  allowedBy?: ToolItem['allowedBy'];
  /** Phase 15e: a built-in tool's outcome in plain words ("Takvime bakıldı · 3 toplantı"); it names the step once done */
  label?: string;
}

export type CurrentStep =
  | { kind: 'tool'; step: StepView; more: number }
  | { kind: 'thinking'; phase: ThinkPhase }
  | { kind: 'writing' }
  /** a question card waits for the user's answer, an approval or a plan for their decision */
  | { kind: 'waiting'; for: 'answer' | 'approval' }
  | { kind: 'compacting' }
  | { kind: 'retry'; attempt: number; max: number };

export interface TurnSummary {
  searches: number;
  pages: number;
  /** distinct skills loaded in the turn */
  skills: number;
  /** document searches and reads */
  docs: number;
  files: number;
  commands: number;
  other: number;
}

/** More skills than this in one turn is shown in its list: each one makes the work slower. */
export const MANY_SKILLS = 3;

/**
 * How a finished turn ended, for its line: prepared (a green tick), stopped by
 * the user, stopped because the token budget ran out (D-97), failed, or ended
 * otherwise (a limit, a rejected approval). Only a prepared turn gets the
 * tick; the others are grey ("Durduruldu · 1 dk 50 sn").
 */
export function turnEnding(reason: string | null, errorCode: string | null = null): 'prepared' | 'stopped' | 'budget' | 'failed' | 'ended' {
  if (reason === null || reason === 'completed') return 'prepared';
  if (reason === 'cancelled') return 'stopped';
  if (reason === 'failed') return errorCode === 'quota_exceeded' ? 'budget' : 'failed';
  return 'ended';
}

/**
 * Phase 15e (MsDurumlar): a turn that stopped only because Microsoft 365 waits for the person (connect,
 * reconnect, the company's consent, turned off, unreachable): its card says so, and nothing in it failed
 * for the person to look at. A turn that did other work before the stop keeps its line.
 */
export function stoppedForMicrosoft365(turn: Pick<TurnSteps, 'reason' | 'errorCode' | 'steps'>): boolean {
  return (
    turn.reason === 'failed' &&
    isMicrosoft365TurnCode(turn.errorCode) &&
    turn.steps.every((step) => step.tool !== null && microsoft365ToolOf(step.tool) !== null && step.status === 'failed')
  );
}

/**
 * Phase 16a (D-265; B2cTokenBitti, B2cGunlukBitti): a turn that stopped before its first step because the person's
 * daily allowance is used up. With plans its question waits and goes on by itself; the allowance card alone says so.
 */
export function waitsForAllowance(turn: Pick<TurnSteps, 'reason' | 'errorCode' | 'steps'>): boolean {
  return turn.reason === 'failed' && turn.errorCode === 'quota_exceeded' && turn.steps.length === 0;
}

/**
 * Whether a turn has its steps line: while it runs, once it has steps or
 * text, and always when it did not finish, also if it was stopped before
 * its first step (the line keeps its place: "Durduruldu · 5 sn"). Not above
 * the card of a turn that only waits for Microsoft 365 (MsDurumlar).
 */
export function showsLine(turn: Pick<TurnSteps, 'running' | 'steps' | 'startedAt' | 'reason' | 'errorCode'>, hasText: boolean): boolean {
  if (!turn.running && !hasText && stoppedForMicrosoft365(turn)) return false;
  return turn.running || turn.steps.length > 0 || hasText || (turn.startedAt !== null && turnEnding(turn.reason) !== 'prepared');
}

export interface TurnSteps {
  runId: string;
  running: boolean;
  startedAt: string | null;
  completedAt: string | null;
  /** how the turn ended (`turn.completed`): a stopped turn is not "prepared" */
  reason: string | null;
  /** why a failed turn failed (a used-up budget reads differently, D-97) */
  errorCode: string | null;
  /** 16b.1: why a `no_visible_reply` turn got no answer: the line says it and opens to the tries */
  failure: TurnFailureDto | null;
  /** finished steps in order (unreadable pages of a batch grouped after the read ones) */
  steps: StepView[];
  /** the step under way, while the turn runs */
  current: CurrentStep | null;
  summary: TurnSummary;
  /**
   * Phase 15e (MsTakvimYatay, MsMailListe, MsMailEk): a finished turn whose only tool steps are Microsoft 365
   * steps, all done, says what they did: "Takvime bakıldı · 0,6 sn". Their labels in order; null otherwise.
   * With `prepared`, the labels of the steps done before the card (maybe none).
   */
  labels: string[] | null;
  /**
   * Part 3 (MsSohbetOnay, MsToplantiOnay): a running turn that waits only for the locked card of a Microsoft 365 write
   * tool, after Microsoft 365 steps that are all done: the line says what was done and what the card holds
   * ("Outlook'ta arandı · 1 mail bulundu · taslak yazıldı") instead of "Onayınızı bekliyor". The write tool; null otherwise.
   */
  prepared: Microsoft365WriteTool | null;
}

type ToolItem = Extract<ChatItem, { kind: 'tool' }>;
type AssistantItem = Extract<ChatItem, { kind: 'assistant' }>;
type SkillItem = Extract<ChatItem, { kind: 'skill' }>;
type DocWaitItem = Extract<ChatItem, { kind: 'docwait' }>;

/** Calls that have a card of their own (a question, a plan, the task list) are not steps. */
const CARD_TOOLS = new Set(['ask_user_question', 'exit_plan_mode', 'todo_write']);

const KIND: Record<string, StepKind> = {
  web_search: 'search',
  web_fetch: 'read',
  doc_search: 'docs',
  doc_read: 'doc',
  skill: 'skill',
  skill_search: 'skill',
  agent: 'helper',
  write_file: 'file',
  append_file: 'file',
  edit: 'file',
  shell: 'command',
  shell_jobs: 'command',
  run_code: 'command',
  read_file: 'look',
  ls: 'look',
  glob: 'look',
  grep: 'look',
};

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function args(item: ToolItem): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(item.arguments || '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

function subjectOf(item: ToolItem): string {
  const a = args(item);
  switch (item.tool) {
    case 'web_search':
    case 'doc_search':
    case 'skill_search':
      return text(a.query);
    case 'web_fetch':
      return hostOf(text(a.url));
    case 'doc_read':
      return Array.isArray(a.document) ? a.document.join(', ') : text(a.document);
    case 'skill':
      return text(a.name);
    case 'agent':
      return text(a.description);
    case 'write_file':
    case 'append_file':
    case 'edit':
    case 'read_file':
    case 'ls':
      return text(a.path) || '.';
    case 'glob':
    case 'grep':
      return text(a.pattern);
    case 'shell':
      return text(a.description) || text(a.command);
    case 'run_code':
      return text(a.purpose);
    default:
      return '';
  }
}

/** "1. Title\n   https://…" lines of a web search result (the model's view of it). */
export function parseSearch(result: string | null): { results: SearchHit[]; more: number } {
  if (!result) return { results: [], more: 0 };
  const results: SearchHit[] = [];
  for (const match of result.matchAll(/^\d+\. (.+)\n {3}(https?:\/\/\S+)/gm)) {
    const [, title = '', url = ''] = match;
    results.push({ title: title.trim(), url, host: hostOf(url) });
  }
  const shown = 3;
  return { results: results.slice(0, shown), more: Math.max(0, results.length - shown) };
}

/** "URL: …\nTitle: …\nRetrieved: …\n\n<text>" of a page the model read. */
export function parsePage(result: string | null, fallbackUrl: string): { url: string; title: string | null; excerpt: string; status: number | null } {
  const value = result ?? '';
  const url = /^URL: (\S+)/m.exec(value)?.[1] ?? fallbackUrl;
  const title = /^Title: (.+)$/m.exec(value)?.[1]?.trim() ?? null;
  const status = Number(/^Status: (\d{3})$/m.exec(value)?.[1] ?? '') || null;
  const body = value.split(/\n\n/).slice(1).join('\n\n').replace(/\s+/g, ' ').trim();
  return { url, title, excerpt: body.slice(0, 280) + (body.length > 280 ? '…' : ''), status };
}

function byStatus(status: number): Unreadable {
  if (status === 401 || status === 403 || status === 429) return 'closed';
  if (status === 404 || status === 410) return 'notFound';
  return status >= 500 ? 'serverError' : 'other';
}

function unreadableReason(item: ToolItem, status: number | null): Unreadable | null {
  if (item.status === 'running') return null;
  if (item.status === 'failed') {
    const http = /^http_(\d{3})$/.exec(item.errorCode ?? '')?.[1];
    if (http) return byStatus(Number(http));
    switch (item.errorCode) {
      case 'network':
        return 'down';
      case 'timeout':
        return 'timeout';
      case 'invalid_url':
        return 'invalid';
      case 'blocked':
        return 'blocked';
      case 'too_many_redirects':
        return 'redirects';
      default:
        return 'other';
    }
  }
  if (status === null || status < 400) return null;
  return byStatus(status);
}

/** The pages a turn could read (an unreadable page is not a source). */
export function readSources(items: ChatItem[], sources: SourceView[]): SourceView[] {
  const failed = new Set(items.filter((i): i is ToolItem => i.kind === 'tool' && i.tool === 'web_fetch' && i.status === 'failed').map((i) => i.callId));
  return sources.filter((source) => !source.callId || !failed.has(source.callId));
}

function between(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const ms = Date.parse(to) - Date.parse(from);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

function toolStep(item: ToolItem, sources: SourceView[]): StepView {
  const kind = KIND[item.tool] ?? 'tool';
  let details: StepDetails | null = null;
  let unreadable: Unreadable | null = null;
  if (item.tool === 'web_search' && item.status === 'done') {
    const parsed = parseSearch(item.result);
    if (parsed.results.length > 0) details = { kind: 'results', ...parsed };
  } else if (item.tool === 'web_fetch') {
    const url = text(args(item).url);
    const page = parsePage(item.result, url);
    unreadable = unreadableReason(item, page.status);
    if (item.status === 'done' && !unreadable) {
      const source = sources.find((s) => s.callId === item.callId);
      details = { kind: 'page', url: page.url, title: source?.title ?? page.title, excerpt: page.excerpt };
    }
  } else if ((item.tool === 'doc_search' || item.tool === 'doc_read') && item.status === 'done' && item.result) {
    details = { kind: 'text', text: item.result.slice(0, 600) + (item.result.length > 600 ? '…' : '') };
  }
  return {
    id: item.callId,
    kind,
    status: item.status,
    tool: item.tool,
    subject: subjectOf(item),
    title: item.title,
    durationMs: item.durationMs,
    unreadable,
    count: 1,
    details,
    ...(item.allowedBy ? { allowedBy: item.allowedBy } : {}),
    ...(item.label ? { label: item.label } : {}),
  };
}

/** What a skill step opens to, when there is something to say. */
function skillDetails(item: SkillItem): StepDetails | null {
  const grants = [...item.grants.commands, ...(item.grants.ownScripts ? ['*'] : [])];
  if (item.parts <= 1 && grants.length === 0 && !item.reused && item.invokedBy !== 'user') return null;
  return { kind: 'skill', part: item.part, parts: item.parts, grants, reused: item.reused, byUser: item.invokedBy === 'user' };
}

/** D-92: waiting for documents still being read, as a step of the line. */
function docWaitStep(item: DocWaitItem): StepView {
  return {
    id: `docwait:${item.seq}`,
    kind: 'prepare',
    status: item.status,
    tool: 'doc_wait',
    subject: item.documents.join(', '),
    title: '',
    durationMs: item.waitedMs,
    unreadable: null,
    count: 1,
    details: null,
  };
}

/** Within a finished batch of reads, the read pages first, then the unreadable ones as one quiet line. */
function groupReads(steps: StepView[]): StepView[] {
  const out: StepView[] = [];
  let batch: StepView[] = [];
  const flush = () => {
    if (batch.length === 0) return;
    const running = batch.some((s) => s.status === 'running');
    const failed = batch.filter((s) => s.unreadable);
    if (running || failed.length < 2) {
      out.push(...batch.filter((s) => !s.unreadable || running), ...(running ? [] : failed));
    } else {
      const reasons = new Set(failed.map((s) => s.unreadable));
      const [first] = failed;
      out.push(...batch.filter((s) => !s.unreadable), {
        ...(first as StepView),
        id: `${first?.id ?? 'read'}+${failed.length}`,
        count: failed.length,
        unreadable: reasons.size === 1 ? (first?.unreadable ?? 'other') : 'other',
        durationMs: failed.reduce((sum, s) => sum + (s.durationMs ?? 0), 0),
      });
    }
    batch = [];
  };
  for (const step of steps) {
    if (step.kind === 'read') batch.push(step);
    else {
      flush();
      out.push(step);
    }
  }
  flush();
  return out;
}

export interface LiveState {
  approvals: Record<string, ApprovalView>;
  drafts: Record<string, string>;
  reasoningDrafts: Record<string, string>;
  compacting: string | null;
  retry: ChatState['retry'];
}

/** The steps of one turn from its items (in order) and, while it runs, the live state. */
export function turnSteps(runId: string, items: ChatItem[], state: Pick<ChatState, 'runs' | 'sources' | 'activeRunId' | 'running'> & LiveState): TurnSteps {
  const run = state.runs[runId];
  const running = state.running && state.activeRunId === runId;
  const assistants = items.filter((i): i is AssistantItem => i.kind === 'assistant');
  // the answer: the turn's last message with text (or answer cards, D-197), once the turn is over
  const answer = running ? null : [...assistants].reverse().find((a) => a.text.trim().length > 0 || a.cards.length > 0) ?? null;
  const sources = state.sources.filter((s) => s.runId === runId);
  const raw: StepView[] = [];
  // D-77: reasoning is named by where it falls; its own words are only in its details
  let toolSeen = false;
  const think = (id: string, text: string, durationMs: number | null): StepView => ({
    id: `think:${id}`,
    kind: 'think',
    status: 'done',
    tool: null,
    subject: '',
    title: '',
    durationMs,
    unreadable: null,
    count: 1,
    details: { kind: 'thought', text },
    phase: toolSeen ? 'evaluate' : 'examine',
  });
  for (const item of items) {
    if (item.kind === 'assistant') {
      const durationMs = between(item.startedAt, item.createdAt);
      if (item === answer) {
        // the answer's own reasoning is a step of its own; the message's time is split where the text began
        const writing = item.reasoning && durationMs !== null && item.writingMs !== null ? Math.min(item.writingMs, durationMs) : null;
        if (item.reasoning) raw.push(think(item.messageId, item.reasoning, writing !== null && durationMs !== null ? durationMs - writing : null));
        raw.push({ id: `answer:${item.messageId}`, kind: 'answer', status: 'done', tool: null, subject: '', title: '', durationMs: writing ?? durationMs, unreadable: null, count: 1, details: null });
      } else if (item.reasoning) {
        raw.push(think(item.messageId, item.reasoning, durationMs));
      }
    } else if (item.kind === 'tool' && !CARD_TOOLS.has(item.tool) && item.errorCode !== 'search_first') {
      // D-216: a guessed address the model was told to search for first is not a step of the user's
      const step = toolStep(item, sources);
      raw.push(step);
      // loading a skill brings instructions, not findings
      if (step.kind !== 'skill') toolSeen = true;
    } else if (item.kind === 'skill') {
      // the model's load is its tool call's step; a skill the user ran with /name is a step of its own
      const call = item.callId ? raw.find((s) => s.id === item.callId) : undefined;
      if (call) call.details = skillDetails(item);
      else if (!item.callId) {
        raw.push({ id: `skill:${item.seq}`, kind: 'skill', status: 'done', tool: 'skill', subject: item.name, title: '', durationMs: null, unreadable: null, count: 1, details: skillDetails(item) });
      }
    } else if (item.kind === 'docwait') {
      raw.push(docWaitStep(item));
    }
  }
  const finished = raw.filter((s) => s.status !== 'running');
  const steps = groupReads(running ? finished : raw);
  const tools = raw.filter((s) => s.tool !== null);
  const summary: TurnSummary = {
    searches: tools.filter((s) => s.kind === 'search').length,
    pages: tools.filter((s) => s.kind === 'read').length,
    skills: new Set(tools.filter((s) => s.tool === 'skill' && s.status !== 'failed').map((s) => s.subject)).size,
    docs: tools.filter((s) => s.kind === 'docs' || s.kind === 'doc').length,
    files: tools.filter((s) => s.kind === 'file').length,
    commands: tools.filter((s) => s.kind === 'command').length,
    other: tools.filter((s) => s.kind === 'tool' || s.kind === 'helper' || s.kind === 'look').length,
  };
  let current: CurrentStep | null = null;
  if (running) {
    const active = raw.filter((s) => s.status === 'running');
    const waiting = Object.values(state.approvals).filter((a) => a.status === 'pending' && a.runId === runId);
    const latest = active.at(-1);
    if (waiting.length > 0) current = { kind: 'waiting', for: waiting.some((a) => a.kind === 'question') ? 'answer' : 'approval' };
    else if (latest) current = { kind: 'tool', step: latest, more: active.length - 1 };
    else if (state.compacting) current = { kind: 'compacting' };
    else if (state.retry) current = { kind: 'retry', attempt: state.retry.attempt, max: state.retry.max };
    else if (Object.values(state.drafts).some((d) => d.length > 0)) current = { kind: 'writing' };
    else current = { kind: 'thinking', phase: toolSeen ? 'evaluate' : 'examine' };
  }
  // a turn that only read Microsoft 365 names what it read instead of counting "1 işlem" (no StepKind of its own)
  const named = (list: StepView[]) => list.every((s) => s.tool !== null && microsoft365ToolOf(s.tool) !== null && s.status === 'done' && s.label);
  const before = tools.filter((s) => s.status === 'done');
  const writing = running ? waitingWrite(raw, state.approvals, runId) : null;
  const prepared = writing && named(before) ? writing : null;
  const labels = prepared ? before.map((s) => s.label as string) : !running && tools.length > 0 && named(tools) ? tools.map((s) => s.label as string) : null;
  return {
    runId,
    running,
    startedAt: run?.startedAt ?? null,
    completedAt: run?.completedAt ?? null,
    reason: run?.reason ?? null,
    errorCode: run?.errorCode ?? null,
    failure: run?.failure ?? null,
    steps,
    current,
    summary,
    labels,
    prepared,
  };
}

/**
 * The Microsoft 365 write tool whose card is all a running turn waits for: its one unfinished step, with a pending
 * tool card of the same call and nothing else pending in the turn.
 */
function waitingWrite(raw: StepView[], approvals: Record<string, ApprovalView>, runId: string): Microsoft365WriteTool | null {
  const open = raw.filter((s) => s.status === 'running');
  const [step] = open;
  if (open.length !== 1 || !step?.tool) return null;
  const tool = microsoft365ToolOf(step.tool);
  if (!isMicrosoft365WriteTool(tool)) return null;
  const waiting = Object.values(approvals).filter((a) => a.status === 'pending' && a.runId === runId);
  return waiting.length === 1 && waiting[0]?.kind === 'tool' && waiting[0].callId === step.id ? tool : null;
}

/** A conversation as blocks: standalone lines and the turns, each turn with all its items. */
export type Block = { kind: 'item'; item: ChatItem } | { kind: 'turn'; runId: string; items: ChatItem[] };

export function runOf(item: ChatItem, approvals: Record<string, ApprovalView>): string | null {
  switch (item.kind) {
    case 'assistant':
    case 'tool':
    case 'skill':
    case 'docwait':
      return item.runId || null;
    case 'artifact':
    case 'plan':
    case 'notice':
    case 'preview':
      return item.runId;
    case 'approval':
      return approvals[item.approvalId]?.runId ?? null;
    default:
      return null;
  }
}

export function blocks(state: Pick<ChatState, 'items' | 'approvals' | 'runs' | 'activeRunId' | 'running'>): Block[] {
  const out: Block[] = [];
  const turns = new Map<string, Extract<Block, { kind: 'turn' }>>();
  for (const item of state.items) {
    const runId = runOf(item, state.approvals);
    if (!runId) {
      out.push({ kind: 'item', item });
      continue;
    }
    const turn = turns.get(runId);
    if (turn) turn.items.push(item);
    else {
      const created = { kind: 'turn' as const, runId, items: [item] };
      turns.set(runId, created);
      out.push(created);
    }
  }
  // a turn that has nothing to show yet stands after the message it answers
  const active = state.running ? state.activeRunId : null;
  if (active && !turns.has(active)) {
    const seqs = state.runs[active]?.inputSeqs ?? [];
    const last = Math.max(0, ...seqs);
    const at = out.findIndex((b) => b.kind === 'item' && b.item.kind === 'user' && b.item.seq === last);
    const block = { kind: 'turn' as const, runId: active, items: [] };
    if (at >= 0) out.splice(at + 1, 0, block);
    else out.push(block);
  }
  return out;
}
